import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { CATEGORIE, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Save, Upload, X } from "lucide-react";

export default function ProductFormPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const editing = !!id;
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    codice_fornitore: "", descrizione: "", marca: "", categoria: "",
    compatibilita: "", posizione: "", quantita_iniziale: 0,
    quantita_minima: 0, prezzo_acquisto: 0, note: "", foto: "",
  });

  useEffect(() => {
    if (!editing) return;
    (async () => {
      const { data } = await api.get(`/products/${id}`);
      setForm({
        codice_fornitore: data.codice_fornitore, descrizione: data.descrizione,
        marca: data.marca, categoria: data.categoria,
        compatibilita: data.compatibilita || "", posizione: data.posizione || "",
        quantita_iniziale: data.giacenza, quantita_minima: data.quantita_minima,
        prezzo_acquisto: data.prezzo_acquisto, note: data.note || "", foto: data.foto || "",
      });
    })();
  }, [id, editing]);

  const update = (k, v) => setForm(s => ({ ...s, [k]: v }));

  const onPhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1.5 * 1024 * 1024) { toast.error("Immagine troppo grande (max 1.5MB)"); return; }
    const r = new FileReader();
    r.onload = () => update("foto", r.result);
    r.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editing) {
        await api.put(`/products/${id}`, {
          codice_fornitore: form.codice_fornitore, descrizione: form.descrizione,
          marca: form.marca, categoria: form.categoria, compatibilita: form.compatibilita,
          posizione: form.posizione, quantita_minima: parseInt(form.quantita_minima || 0),
          prezzo_acquisto: parseFloat(form.prezzo_acquisto || 0), note: form.note, foto: form.foto,
        });
        toast.success("Prodotto aggiornato");
        nav(`/prodotti/${id}`);
      } else {
        const { data } = await api.post("/products", {
          ...form,
          quantita_iniziale: parseInt(form.quantita_iniziale || 0),
          quantita_minima: parseInt(form.quantita_minima || 0),
          prezzo_acquisto: parseFloat(form.prezzo_acquisto || 0),
        });
        toast.success(`Prodotto creato: ${data.codice_interno}`);
        nav(`/prodotti/${data.id}`);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="p-4 md:p-8 max-w-4xl mx-auto">
      <button onClick={()=>nav(-1)} className="text-xs uppercase tracking-widest text-zinc-400 hover:text-white mb-3 flex items-center gap-1.5">
        <ArrowLeft size={14} /> Indietro
      </button>
      <h1 className="font-display text-4xl font-black uppercase tracking-tight mb-8">
        {editing ? "Modifica Prodotto" : "Nuovo Prodotto"}
      </h1>

      <form onSubmit={submit} className="gm-card p-6 space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Codice fornitore / OEM *" testId="form-codice-fornitore">
            <input required value={form.codice_fornitore} onChange={e=>update("codice_fornitore", e.target.value)} className="gm-input font-mono-data" />
          </Field>
          <Field label="Descrizione *" testId="form-descrizione">
            <input required value={form.descrizione} onChange={e=>update("descrizione", e.target.value)} className="gm-input" />
          </Field>
          <Field label="Marca / Fornitore *" testId="form-marca">
            <input required value={form.marca} onChange={e=>update("marca", e.target.value)} className="gm-input" />
          </Field>
          <Field label="Categoria *" testId="form-categoria">
            <select required value={form.categoria} onChange={e=>update("categoria", e.target.value)} className="gm-input">
              <option value="">Seleziona...</option>
              {CATEGORIE.map(c=><option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Compatibilità macchina" testId="form-compatibilita">
            <input value={form.compatibilita} onChange={e=>update("compatibilita", e.target.value)} className="gm-input" placeholder="es. Epiroc SB552, Wacker DPU" />
          </Field>
          <Field label="Posizione magazzino" testId="form-posizione">
            <input value={form.posizione} onChange={e=>update("posizione", e.target.value)} className="gm-input font-mono-data" placeholder="es. A-03-02" />
          </Field>
          {!editing && (
            <Field label="Quantità iniziale" testId="form-quantita-iniziale">
              <input type="number" min="0" value={form.quantita_iniziale} onChange={e=>update("quantita_iniziale", e.target.value)} className="gm-input font-mono-data" />
            </Field>
          )}
          <Field label="Quantità minima (sotto scorta)" testId="form-quantita-minima">
            <input type="number" min="0" value={form.quantita_minima} onChange={e=>update("quantita_minima", e.target.value)} className="gm-input font-mono-data" />
          </Field>
          <Field label="Prezzo acquisto (€)" testId="form-prezzo">
            <input type="number" step="0.01" min="0" value={form.prezzo_acquisto} onChange={e=>update("prezzo_acquisto", e.target.value)} className="gm-input font-mono-data" />
          </Field>
        </div>

        <Field label="Note" testId="form-note">
          <textarea rows="3" value={form.note} onChange={e=>update("note", e.target.value)} className="gm-input py-2 h-auto resize-none" />
        </Field>

        <div>
          <label className="block text-xs font-bold uppercase tracking-widest text-zinc-400 mb-2">Foto prodotto</label>
          {form.foto ? (
            <div className="relative inline-block">
              <img src={form.foto} alt="anteprima" className="h-40 object-cover border border-zinc-800" />
              <button type="button" onClick={()=>update("foto", "")} className="absolute -top-2 -right-2 bg-[#FF3B30] text-white p-1 rounded-full">
                <X size={14} />
              </button>
            </div>
          ) : (
            <label className="inline-flex items-center gap-2 px-4 py-3 border border-dashed border-zinc-700 text-zinc-400 cursor-pointer hover:border-[#4CAF50] hover:text-[#4CAF50]" data-testid="form-foto-upload">
              <Upload size={16} /> Carica foto
              <input type="file" accept="image/*" className="hidden" onChange={onPhoto} />
            </label>
          )}
        </div>

        <div className="flex gap-3 pt-3 border-t border-zinc-800">
          <button type="submit" disabled={busy} data-testid="form-submit-button" className="gm-btn-primary h-12 px-6 rounded-sm flex items-center gap-2">
            <Save size={18} /> {busy ? "Salvataggio..." : (editing ? "Aggiorna" : "Crea prodotto")}
          </button>
          <button type="button" onClick={()=>nav(-1)} className="h-12 px-6 border border-zinc-700 text-zinc-300 uppercase text-xs tracking-widest font-bold hover:border-zinc-500">
            Annulla
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children, testId }) {
  return (
    <div data-testid={testId}>
      <label className="block text-xs font-bold uppercase tracking-widest text-zinc-400 mb-2">{label}</label>
      {children}
    </div>
  );
}
